import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, ArrowLeft, Inbox, Eye, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: "New", className: "bg-[rgba(240,180,41,0.15)] text-[color:var(--gs-gold-400)] border-[color:var(--gs-line)]" },
  in_progress: { label: "In Progress", className: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  closed: { label: "Closed", className: "bg-white/10 text-white/60 border-white/15" },
};

const FILTERS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "closed", label: "Closed" },
];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const res = await axios.get(`${API}/inquiries`, { params });
      setInquiries(res.data);
    } catch (e) {
      toast.error("Failed to load inquiries. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}`, { status });
      setInquiries((prev) =>
        prev.map((inq) => (inq.id === id ? { ...inq, status } : inq))
      );
      toast.success(`Status updated to "${STATUS_META[status].label}"`);
    } catch (e) {
      toast.error("Failed to update status.");
    }
  };

  const deleteInquiry = async (id) => {
    try {
      await axios.delete(`${API}/inquiries/${id}`);
      setInquiries((prev) => prev.filter((inq) => inq.id !== id));
      toast.success("Inquiry deleted.");
    } catch (e) {
      toast.error("Failed to delete inquiry.");
    }
  };

  const newCount = inquiries.filter((i) => i.status === "new").length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--gs-navy-900)" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[color:var(--gs-line)] backdrop-blur bg-[rgba(10,14,26,0.75)]">
        <div className="mx-auto flex max-w-[var(--container-max)] items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo-small.png" alt="GrowthSync Solutions" className="h-10 w-10 rounded-md" />
            <div>
              <h1 className="font-display text-lg text-white" data-testid="admin-page-title">
                Inquiry Dashboard
              </h1>
              <p className="text-xs text-white/50">GrowthSync Solutions — Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchInquiries}
              data-testid="admin-refresh-button"
              className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
            >
              <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
            </Button>
            <Button
              asChild
              size="sm"
              variant="ghost"
              className="text-white/70 hover:bg-white/5 hover:text-white"
            >
              <Link to="/" data-testid="admin-back-to-site-link">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to site
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[var(--container-max)] px-4 py-8 sm:px-6">
        {/* Filter bar */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                data-testid={`admin-filter-${f.value}`}
                className={`rounded-full border px-4 py-1.5 text-sm ${
                  filter === f.value
                    ? "border-[color:var(--gs-gold-400)] bg-[rgba(240,180,41,0.15)] text-[color:var(--gs-gold-400)]"
                    : "border-white/15 bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <p className="text-sm text-white/50" data-testid="admin-inquiry-count">
            {inquiries.length} inquir{inquiries.length === 1 ? "y" : "ies"}
            {filter === "all" && newCount > 0 && (
              <span className="ml-2 text-[color:var(--gs-gold-400)]">({newCount} new)</span>
            )}
          </p>
        </div>

        {/* Table */}
        <Card className="overflow-hidden border-white/10 bg-[color:var(--gs-glass)] shadow-[var(--shadow-elev-1)]">
          {loading ? (
            <div className="space-y-3 p-6">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full bg-white/5" />
              ))}
            </div>
          ) : inquiries.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
              <Inbox className="h-12 w-12 text-white/20" />
              <p className="text-white/60" data-testid="admin-empty-state">
                No inquiries {filter !== "all" ? `with status "${FILTERS.find((f) => f.value === filter)?.label}"` : "yet"}.
              </p>
              <p className="text-sm text-white/40">
                New quotation requests from the website will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-inquiries-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/60">Date</TableHead>
                    <TableHead className="text-white/60">Name</TableHead>
                    <TableHead className="text-white/60">Contact</TableHead>
                    <TableHead className="text-white/60">Service</TableHead>
                    <TableHead className="text-white/60">Message</TableHead>
                    <TableHead className="text-white/60">Status</TableHead>
                    <TableHead className="text-right text-white/60">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inquiries.map((inq) => (
                    <TableRow
                      key={inq.id}
                      data-testid="admin-inquiry-row"
                      className={`border-white/10 hover:bg-white/[0.03] ${
                        inq.status === "new"
                          ? "border-l-2 border-l-[color:var(--gs-gold-400)]"
                          : inq.status === "closed"
                          ? "opacity-60"
                          : ""
                      }`}
                    >
                      <TableCell className="whitespace-nowrap text-sm text-white/60">
                        {formatDate(inq.created_at)}
                      </TableCell>
                      <TableCell className="font-medium text-white">{inq.name}</TableCell>
                      <TableCell>
                        <div className="text-sm text-white/80">{inq.email}</div>
                        <div className="text-xs text-white/50">{inq.phone}</div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="whitespace-nowrap border-[color:var(--gs-line)] bg-[rgba(240,180,41,0.08)] text-[color:var(--gs-gold-400)]"
                        >
                          {inq.service}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        <p className="truncate text-sm text-white/70">{inq.message}</p>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={inq.status}
                          onValueChange={(val) => updateStatus(inq.id, val)}
                        >
                          <SelectTrigger
                            data-testid="admin-inquiry-status-select"
                            className="h-8 w-[130px] border-white/15 bg-white/5 text-sm text-white"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="border-white/15 bg-[color:var(--gs-navy-850)] text-white">
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid="admin-view-inquiry-button"
                                className="h-8 w-8 text-white/60 hover:bg-white/10 hover:text-white"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="border-white/15 bg-[color:var(--gs-navy-850)] text-white sm:max-w-lg">
                              <DialogHeader>
                                <DialogTitle className="font-display text-white">
                                  Inquiry from {inq.name}
                                </DialogTitle>
                                <DialogDescription className="text-white/50">
                                  {formatDate(inq.created_at)} — {inq.service}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-3 text-sm">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <p className="text-white/40">Email</p>
                                    <p className="break-all text-white/90">{inq.email}</p>
                                  </div>
                                  <div>
                                    <p className="text-white/40">Phone</p>
                                    <p className="text-white/90">{inq.phone}</p>
                                  </div>
                                </div>
                                <div>
                                  <p className="mb-1 text-white/40">Requirements / Message</p>
                                  <p className="whitespace-pre-wrap rounded-lg border border-white/10 bg-white/5 p-3 text-white/90">
                                    {inq.message}
                                  </p>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteInquiry(inq.id)}
                            data-testid="admin-delete-inquiry-button"
                            className="h-8 w-8 text-white/40 hover:bg-red-500/10 hover:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
